// AddPersonModal.jsx – Neue Person hinzufügen ODER bestehende bearbeiten
// Im Edit-Modus werden bestehende Beziehungen vorgeladen und können geändert werden.
import React, { useState, useRef, useMemo } from 'react';
import { EditedPhoto } from './EditedPhoto';
import { PhotoEditor } from './PhotoEditor';
import { splitName } from '../utils/photoEdit';
import { defaultSecondParent } from '../utils/treeRules';
import './Modal.css';

// Auswahl „Beziehung“ im Formular. Die Werte werden in treeRules.js (buildRelationships)
// in gespeicherte Beziehungen übersetzt.
const RELATION_OPTIONS = [
  { value: 'child-of',      label: 'Kind von' },
  { value: 'parent-of',     label: 'Elternteil von' },
  { value: 'sibling-of',    label: 'Geschwister von' },
  { value: 'partner-of',    label: 'Partner von' },
  { value: 'ex-partner-of', label: 'Expartner von' },
];

// Eine leere Formularzeile für eine Beziehung.
function emptyRelation() {
  return { relationType: '', relatedTo: '', secondParent: '' };
}

/** Bestehende Relationships einer Person → Formular-Array */
// Für „Person bearbeiten“: die gespeicherten Beziehungen wieder in Formularzeilen
// umwandeln (z.B. zwei Eltern → eine Zeile „Kind von A“ mit „Elternteil 2: B“).
function relsToForm(personId, allRels, persons) {
  const myRels = allRels.filter(r => r.from === personId || r.to === personId);
  const rows = [];

  myRels.forEach(r => {
    if (r.type === 'partner') {
      const otherId = r.from === personId ? r.to : r.from;
      rows.push({ relationType: 'partner-of', relatedTo: otherId, secondParent: '' });
    } else if (r.type === 'ex-partner') {
      const otherId = r.from === personId ? r.to : r.from;
      rows.push({ relationType: 'ex-partner-of', relatedTo: otherId, secondParent: '' });
    } else if (r.type === 'parent-child') {
      if (r.from === personId) {
        // Ich bin Elternteil → "Elternteil von <Kind>"
        rows.push({ relationType: 'parent-of', relatedTo: r.to, secondParent: '' });
      } else {
        // r.to === personId → Ich bin Kind von r.from
        // Suche ob schon eine "Kind von" Zeile existiert (zweiter Elternteil)
        const existing = rows.find(
          row => row.relationType === 'child-of' &&
                 allRels.some(r2 => r2.type === 'parent-child' && r2.to === personId &&
                   (r2.from === row.relatedTo || r2.from === row.secondParent))
        );
        if (existing && !existing.secondParent) {
          existing.secondParent = r.from;
        } else {
          rows.push({ relationType: 'child-of', relatedTo: r.from, secondParent: '' });
        }
      }
    }
  });

  // Deduplizieren: "Kind von" Zeilen zusammenfassen
  const merged = [];
  const otherRows   = rows.filter(r => r.relationType !== 'child-of');

  // Eltern des personId zusammenfassen
  const parentIds = allRels
    .filter(r => r.type === 'parent-child' && r.to === personId)
    .map(r => r.from);

  if (parentIds.length > 0) {
    // Im Edit-Modus '__none__' statt '' damit kein Auto-Detect läuft
    merged.push({ relationType: 'child-of', relatedTo: parentIds[0], secondParent: parentIds[1] || '__none__' });
  }
  merged.push(...otherRows);

  return merged.length > 0 ? merged : [emptyRelation()];
}

// ── Foto verkleinern ─────────────────────────────────────────────────────
// Die KÜRZERE Seite wird auf höchstens 2000 px verkleinert (Hochformat: Breite,
// Querformat: Höhe) – genug für den Druck, aber 5–10× kleiner als ein Handyfoto.
// Kleinere Fotos bleiben unverändert in der Grösse. Ergebnis: JPEG.
const PHOTO_SHORT_SIDE = 2000;
const PHOTO_QUALITY = 0.88;

// Datei als Text („data:image/jpeg;base64,…“) einlesen.
function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

// Wird beim Hochladen jedes Fotos verwendet (auch in App.jsx). Spart Speicherplatz
// und macht Speichern/Laden viel schneller.
export async function shrinkPhoto(file) {
  const original = await readAsDataUrl(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = original; // moderne Browser berücksichtigen dabei die Drehung (EXIF)
    });
    const w = img.naturalWidth, h = img.naturalHeight;
    if (!w || !h) return original;
    const factor = Math.min(1, PHOTO_SHORT_SIDE / Math.min(w, h));
    const cw = Math.round(w * factor), ch = Math.round(h * factor);
    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';            // transparente PNGs → weisser Hintergrund
    ctx.fillRect(0, 0, cw, ch);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, cw, ch);
    const result = canvas.toDataURL('image/jpeg', PHOTO_QUALITY);
    // Falls das Ergebnis (z.B. bei kleinen JPEGs) nicht kleiner ist → Original behalten
    return result.length < original.length ? result : original;
  } catch {
    return original; // Format kann im Browser nicht gelesen werden → unverändert
  }
}

// Vor- und Nachname für das Formular vorbereiten.
function initialNames(p) {
  if (!p) return { firstName: '', lastName: '' };
  const n = splitName(p);
  return { firstName: n.first, lastName: n.last };
}

// Props: persons/relationships = der Stammbaum, onSave(formData, beziehungen),
// onClose, editPerson = Person beim Bearbeiten (sonst neue Person),
// presetRelation = vorausgewählte Beziehung vom „+“-Knopf einer Karte.
export function AddPersonModal({ persons, relationships = [], onClose, onSave, editPerson, presetRelation }) {
  const isEdit = !!editPerson;

  // Formularfelder (beim Bearbeiten mit den bisherigen Werten gefüllt).
  const [form, setForm] = useState({
    ...initialNames(editPerson),
    maidenName: editPerson?.maidenName || '',
    birthDate:  editPerson?.birthDate  || '',
    gender:     editPerson?.gender     || 'unknown',
    photo:      editPerson?.photo      || null,
    photoEdit:  editPerson?.photoEdit  || null,
    photoRetouch: editPerson?.photoRetouch || null,
  });
  const [photoEditor, setPhotoEditor] = useState(null); // { photo, edit } während der Bearbeitung

  // Beziehungszeilen zu Beginn: beim Bearbeiten aus den gespeicherten Beziehungen,
  // beim „+“-Knopf vorausgefüllt, sonst eine leere Zeile.
  const initialRelations = useMemo(() => {
    if (isEdit) return relsToForm(editPerson.id, relationships, persons);
    if (presetRelation) {
      // Beziehung vorausfüllen basierend auf Quick-Add
      const { personId, opt } = presetRelation;
      if (opt.direction === 'child') {
        // Neue Person ist ein Kind → relationType='child-of', relatedTo=personId
        // Partner von Elternteil 1 automatisch als Elternteil 2 vorausfüllen
        // (ohne aktuellen Partner: der einzige Expartner, siehe defaultSecondParent)
        return [{ relationType: 'child-of', relatedTo: personId, secondParent: defaultSecondParent(personId, relationships) }];
      } else if (opt.direction === 'parent') {
        // Neue Person ist ein Elternteil → relationType='parent-of', relatedTo=personId
        return [{ relationType: 'parent-of', relatedTo: personId, secondParent: '' }];
      } else if (opt.direction === 'sibling') {
        return [{ relationType: 'sibling-of', relatedTo: personId, secondParent: '' }];
      } else if (opt.direction === 'partner') {
        return [{ relationType: 'partner-of', relatedTo: personId, secondParent: '' }];
      } else if (opt.direction === 'ex') {
        return [{ relationType: 'ex-partner-of', relatedTo: personId, secondParent: '' }];
      }
    }
    return [emptyRelation()];
  }, []); // eslint-disable-line

  const [relations, setRelations] = useState(initialRelations);
  const [photoPreview, setPhotoPreview] = useState(editPerson?.photo || null);
  const fileRef = useRef();

  // ── Foto ──────────────────────────────────────────────────────────────
  const [photoProcessing, setPhotoProcessing] = useState(false);
  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = ''; // gleiche Datei nochmals wählbar
    if (!file) return;
    setPhotoProcessing(true);
    try {
      const dataUrl = await shrinkPhoto(file);
      setPhotoEditor({ photo: dataUrl, edit: null, retouch: null }); // direkt in den Bildeditor
    } finally {
      setPhotoProcessing(false);
    }
  };

  // ── Hilfsfunktion: Partner einer Person finden ────────────────────────
  // Aktueller Partner – oder, ohne aktuellen Partner, der einzige Expartner.
  const findPartner = (personId) => defaultSecondParent(personId, relationships);

  // ── Beziehungszeilen ──────────────────────────────────────────────────
  // Eine Beziehungszeile ändern. Wechselt die Art der Beziehung, wird die gewählte Person zurückgesetzt.
  const updateRelation = (index, field, value) => {
    setRelations(prev => prev.map((r, i) => {
      if (i !== index) return r;
      const updated = {
        ...r,
        [field]: value,
        ...(field === 'relationType' ? { relatedTo: '', secondParent: '' } : {}),
      };
      // Auto-fill Elternteil 2 wenn Elternteil 1 gewählt wird und dieser einen Partner hat
      if (field === 'relatedTo' && updated.relationType === 'child-of' && value) {
        updated.secondParent = findPartner(value);
      }
      return updated;
    }));
  };

  const addRelationRow    = () => setRelations(prev => [...prev, emptyRelation()]);
  const removeRelationRow = (index) =>
    setRelations(prev => prev.length === 1 ? [emptyRelation()] : prev.filter((_, i) => i !== index));

  // ── Submit ────────────────────────────────────────────────────────────
  // „Speichern“: Vorname ist Pflicht; nur vollständige Beziehungszeilen werden übergeben.
  // Ob die Grenzen eingehalten sind, prüft App.jsx danach (checkChange).
  const handleSubmit = (e) => {
    e.preventDefault();
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    if (!firstName) return;
    const validRels = relations.filter(r => r.relationType && r.relatedTo);
    onSave({ ...form, firstName, lastName, name: [firstName, lastName].filter(Boolean).join(' ') }, validRels);
  };

  const otherPersons = persons.filter(p => p.id !== editPerson?.id);

  // Anzahl Elternteile einer Person, ohne die gerade bearbeitete Person
  // (deren eigene Beziehungen werden beim Speichern neu aufgebaut).
  const parentCountOf = (id) => new Set(
    relationships
      .filter(r => r.type === 'parent-child' && r.to === id && r.from !== editPerson?.id)
      .map(r => r.from)
  ).size;

  // Kandidaten für das Personen-Dropdown je nach Beziehungstyp:
  // "Elternteil von X" nur, wenn X noch weniger als 2 Elternteile hat
  // (oder in einer anderen Zeile dieses Formulars nicht schon gewählt ist).
  const candidatesFor = (rel) => {
    if (rel.relationType !== 'parent-of') return otherPersons;
    return otherPersons.filter(p => p.id === rel.relatedTo || parentCountOf(p.id) < 2);
  };

  // Beschriftung des Personen-Auswahlfeldes je nach Beziehung.
  const relatedLabel = (type) => ({
    'child-of':      'Elternteil 1',
    'parent-of':     'Kind',
    'sibling-of':    'Geschwister von',
    'partner-of':    'Partner von',
    'ex-partner-of': 'Expartner von',
  }[type] || 'Person');

  // Anzeige: Foto, Stammdaten (Name, Geburtsdatum …), Beziehungen, Knöpfe.
  // Klick auf das Foto öffnet den Bildeditor (PhotoEditor).
  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2>{isEdit ? 'Person bearbeiten' : 'Neue Person hinzufügen'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">

          {/* Foto */}
          <div className="photo-upload-area" style={{ borderRadius: 14 }}
            onClick={() => (form.photo ? setPhotoEditor({ photo: form.photo, edit: form.photoEdit, retouch: form.photoRetouch }) : fileRef.current?.click())}
            title={form.photo ? 'Bild bearbeiten' : 'Foto hochladen'}>
            {form.photo ? (
              <EditedPhoto photo={form.photo} edit={form.photoEdit} alt="Vorschau" className="photo-preview" />
            ) : (
              <div className="photo-placeholder">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                <span>{photoProcessing ? 'Wird verarbeitet…' : 'Foto hochladen'}</span>
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: 'none' }} />
            {photoPreview && (
              <button type="button" className="photo-remove" onClick={(e) => {
                e.stopPropagation();
                setForm(f => ({ ...f, photo: null, photoEdit: null, photoRetouch: null }));
                setPhotoPreview(null);
              }}>×</button>
            )}
          </div>
          {form.photo && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 14, marginTop: -12, marginBottom: 16, fontSize: 13 }}>
              <button type="button" className="link-btn" style={{ border: 'none', background: 'none', color: '#1540dc', cursor: 'pointer', padding: 0 }}
                onClick={() => setPhotoEditor({ photo: form.photo, edit: form.photoEdit, retouch: form.photoRetouch })}>Bild bearbeiten</button>
              <button type="button" className="link-btn" style={{ border: 'none', background: 'none', color: '#1540dc', cursor: 'pointer', padding: 0 }}
                onClick={() => fileRef.current?.click()}>Anderes Foto</button>
            </div>
          )}
          {form.photo && form.photoRetouch && (
            <div style={{ textAlign: 'center', marginTop: -8, marginBottom: 14, fontSize: 12.5, color: '#6b21a8' }}>
              ✋ Wird von Hand bearbeitet (CHF 5.–)
            </div>
          )}

          {/* Stammdaten */}
          <div className="form-grid">
            <div className="form-group">
              <label>Vorname *</label>
              <input type="text" value={form.firstName}
                onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                placeholder="z.B. Maria" required autoFocus={!isEdit} />
            </div>
            <div className="form-group">
              <label>Nachname</label>
              <input type="text" value={form.lastName}
                onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                placeholder="z.B. Müller" />
            </div>
            <div className="form-group">
              <label>Ledigname</label>
              <input type="text" value={form.maidenName}
                onChange={e => setForm(f => ({ ...f, maidenName: e.target.value }))}
                placeholder="z.B. Schmidt" />
            </div>
            <div className="form-group">
              <label>Geschlecht</label>
              <select value={form.gender} onChange={e => setForm(f => ({ ...f, gender: e.target.value }))}>
                <option value="unknown">Unbekannt</option>
                <option value="male">Männlich</option>
                <option value="female">Weiblich</option>
              </select>
            </div>
            <div className="form-group form-group--wide">
              <label>Geburtsdatum</label>
              <input type="date" value={form.birthDate}
                onChange={e => setForm(f => ({ ...f, birthDate: e.target.value }))} />
            </div>
          </div>

          {/* Beziehungen – immer sichtbar, solange andere Personen existieren */}
          {otherPersons.length > 0 && (
            <div className="relation-section">
              <div className="relation-section-header">
                <h3>Beziehungen</h3>
                <button type="button" className="btn-add-relation" onClick={addRelationRow}>
                  + Hinzufügen
                </button>
              </div>

              {relations.map((rel, idx) => (
                <div key={idx} className="relation-row">
                  <div className="relation-row-header">
                    <span className="relation-row-num">Beziehung #{idx + 1}</span>
                    <button type="button" className="relation-row-remove"
                      onClick={() => removeRelationRow(idx)}>×</button>
                  </div>

                  <div className="form-grid">
                    <div className="form-group form-group--wide">
                      <label>Typ</label>
                      <select value={rel.relationType}
                        onChange={e => updateRelation(idx, 'relationType', e.target.value)}>
                        <option value="">— Typ wählen —</option>
                        {RELATION_OPTIONS.filter(o => {
                          // 'child-of' nur einmal erlauben (1 Zeile = max. 2 Elternteile)
                          if (o.value === 'child-of') {
                            const alreadyUsed = relations.some((r, i) => i !== idx && r.relationType === 'child-of');
                            return !alreadyUsed;
                          }
                          return true;
                        }).map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>

                    {rel.relationType && (
                      <div className="form-group form-group--wide">
                        <label>{relatedLabel(rel.relationType)}</label>
                        <select value={rel.relatedTo}
                          onChange={e => updateRelation(idx, 'relatedTo', e.target.value)}>
                          <option value="">— Person auswählen —</option>
                          {candidatesFor(rel).map(p => (
                            <option key={p.id} value={p.id}>
                              {p.name || '(Kein Name)'}
                              {p.maidenName ? ` (geb. ${p.maidenName})` : ''}
                              {p.birthDate  ? ` *${p.birthDate}` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {rel.relationType === 'child-of' && rel.relatedTo && (
                      <div className="form-group form-group--wide">
                        <label>Elternteil 2 <span className="label-optional">(optional)</span></label>
                        <select value={rel.secondParent}
                          onChange={e => updateRelation(idx, 'secondParent', e.target.value)}>
                          <option value="">— Zweiten Elternteil wählen —</option>
                          <option value="__none__">Kein zweiter Elternteil</option>
                          {otherPersons
                            .filter(p => p.id !== rel.relatedTo)
                            .map(p => (
                              <option key={p.id} value={p.id}>
                                {p.name || '(Kein Name)'}
                                {p.maidenName ? ` (geb. ${p.maidenName})` : ''}
                                {p.birthDate  ? ` *${p.birthDate}` : ''}
                              </option>
                            ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn btn--ghost" onClick={onClose}>Abbrechen</button>
            <button type="submit" className="btn btn--primary" disabled={!form.firstName.trim() || photoProcessing}>
              {isEdit ? 'Speichern' : 'Hinzufügen'}
            </button>
          </div>
        </form>
      </div>
      {photoEditor && (
        <PhotoEditor
          photo={photoEditor.photo}
          edit={photoEditor.edit}
          retouch={photoEditor.retouch}
          title={photoEditor.edit || form.photo === photoEditor.photo ? 'Bild bearbeiten' : 'Foto zuschneiden'}
          onCancel={() => setPhotoEditor(null)}
          onConfirm={(edit, retouch) => {
            setForm(f => ({ ...f, photo: photoEditor.photo, photoEdit: edit, photoRetouch: retouch || null }));
            setPhotoPreview(photoEditor.photo);
            setPhotoEditor(null);
          }}
        />
      )}
    </div>
  );
}
